import {Alpha3, BigDecimal, Currency, PricingStrategy, UuidResponseBody} from '@dms/api/shared';

import {Nullish} from 'shared/web';

type PriceReportResponseBody = {
  id: string;
  requestId: string | null;
  url: string;
  country: Alpha3;
  createdAt: string;
};

export type CreatePresetApiResponse = UuidResponseBody;
export type CreateSourcingVehicleForBusinessCaseApiResponse = UuidResponseBody;
export type CreateSourcingVehicleForBusinessCaseApiArg = {
  externalId: string;
  make?: string;
  model?: string;
};
export type SourcingCreatePresetApiArg = {
  createPresetRequestBody: CreatePresetRequestBody;
};
export type GetPresetsListApiResponse = /** status 200  */ PresetListItemResponseBody[];
export type GetPresetsListApiArg = {
  country?: Alpha3;
  currency?: Alpha3;
};
export type UpdatePresetApiResponse = unknown;
export type SourcingUpdatePresetApiArg = {
  id: string;
  updatePresetRequestBody: UpdatePresetRequestBody;
};
export type DeletePresetApiResponse = unknown;
export type SourcingDeletePresetApiArg = {
  id: string;
};
/** Endpoint answers 204 No Content — the updated preset is not returned. */
export type MarkPresetAllSeenApiResponse = void;
export type SourcingMarkPresetAllSeenApiArg = {
  id: string;
};
export type GetPresetApiResponse = /** status 200  */ SourcingPresetResponseBody;
export type GetPresetApiArg = {
  id: string;
};
export type ReorderPresetsApiResponse = /** status 200  */ PresetListItemResponseBody[];
export type ReorderPresetsApiArg = {
  reorderPresetsRequestBody: ReorderPresetsRequestBody;
};
export type SourcingPresetResponseBody = {
  id: string;
  name: string;
  filters: FiltersBody;
  allowNotifications: boolean;
  newCount: number | null;
};
export type FiltersAddressGpsBody = {
  lat?: BigDecimal | null;
  lng?: BigDecimal | null;
};
export type FiltersAddressBody = {
  placeId?: string | null;
  prefix?: string | null;
  street?: string | null;
  orientationNumber?: string | null;
  descriptiveNumber?: string | null;
  city?: string | null;
  zipCode?: string | null;
  postal?: string | null;
  district?: string | null;
  state?: string | null;
  country?: string | null;
  gps?: FiltersAddressGpsBody | null;
};
/** Vehicle category of a sourcing listing, independent of its body style. */
export type SourcingVehicleCategory = 'CAR' | 'VAN';

export type FiltersBody = {
  sellerCountry?: Alpha3[];
  sellerServer?: string[];
  sellerType?: string[];
  avgNumberAds?: number[] | null;
  make?: string[];
  model?: string[];
  fuelType?: string[];
  drive?: string[];
  transmission?: string[];
  bodyType?: string[];
  interiorMaterial?: string[];
  /** Country the vehicle originally comes from (not the seller country). */
  originCountry?: Alpha3[];
  /** `null` or omitted = every category. Persisted so an applied preset keeps filtering by it. */
  vehicleCategory?: SourcingVehicleCategory | null;
  /** Van body length classes (`LENGTH_L1` … `LENGTH_L4`). */
  vehicleLengthCategory?: string[];
  /** Van body height classes (`HEIGHT_H1` … `HEIGHT_H3`). */
  vehicleHeightCategory?: string[];
  selectedColors?: {
    key: string;
    name: string;
    colorCode: string;
  }[];
  year?: number[] | null;
  power?: number[] | null;
  margin?: number[] | null;
  marginPercent?: number[] | null;
  pricePercentile?: number[] | null;
  cubicCapacity?: number[] | null;
  liquidity?: number[] | null;
  mileageRange?: number[] | null;
  daysOnDisplay?: number[] | null;
  priceCZK?: number[] | null;
  /** Sourcing UI price encoding; missing means the legacy 4,000,000 sentinel. */
  priceRangeVersion?: 2;
  numberOfSeats?: number[] | null;
  featureScore?: number[] | null;
  reclaimableVat?: boolean | null;
  features?: string[];
  trimLevels?: string[];
  doorCount?: string[];
  adTitle?: string | null;
  address?: FiltersAddressBody | null;
  radius?: number | null;
  hideSuspicious?: boolean;
  excludeSuspiciousReasons?: string[];
  hitlistOnly?: boolean | null;
  hideBlacklist?: boolean | null;
  carvagoAvailability?: boolean | null;
  isMarginAbsolute?: boolean;
  isPowerInKW?: boolean;
};
export type CreatePresetRequestBody = {
  name: string;
  filters: FiltersBody;
  allowNotifications: boolean;
};
export type PresetListItemResponseBody = {
  id: string;
  name: string;
  position: number;
  allowNotifications: boolean;
  newCount: number | null;
};
export type UpdatePresetRequestBody = {
  name?: string;
  filters?: FiltersBody;
  allowNotifications?: boolean;
};
export type OrderedPresetItem = {
  id: string;
  position: number;
};
export type ReorderPresetsRequestBody = {
  items: OrderedPresetItem[];
};

/**
 * Which user list a CSV export reads. Both are optional on the endpoint and default to the
 * classifieds To buy list, which is what the export served before it covered every list.
 */
export type UserListExportTarget =
  | {
      sourcingType: 'CLASSIFIEDS';
      list: 'TO_BUY' | 'FAVOURITE' | 'COMPARISON' | 'HIDDEN' | 'CARVAGO_BUY';
    }
  | {
      sourcingType: 'AUCTIONS';
      // Carvago buy exists for classifieds only; the backend answers DMS-SU-EX-0003 otherwise.
      list: 'TO_BUY' | 'FAVOURITE' | 'COMPARISON' | 'HIDDEN';
    };

/**
 * The CSV as text plus the name the backend gave it. `fileName` is null only when
 * `Content-Disposition` does not reach the browser, and the caller then falls back to its own.
 */
export type UserListCsvExport = {
  content: string;
  fileName: string | null;
};

export type ExportAllUserToBuyToCsvApiArg = UserListExportTarget & {
  buyerCountry?: Alpha3;
  currency?: Alpha3;
  pricingStrategy?: PricingStrategy;
  margin?: string;
  /** Comma-separated sort fields, for auctions too. */
  orderBy?: string;
};

export type TranslationResponseBody = {
  key: string;
  translation: string;
};

export type FilterCar = {
  make?: string | null;
  model?: string | null;
  year?: number | null;
};

export type FilterColors = {
  color?: string | null;
  colorSpecification?: string | null;
};

export type FloatRange = {
  lte?: number | null;
  gte?: number | null;
};

export type FilterSeller = {
  country?: string[];
  server?: string[];
  types?: string[];
  ranking?: number | null;
};

export type IntegerRange = {
  lte?: number | null;
  gte?: number | null;
};

export type GetTopSellingRequestBody = {
  price?: IntegerRange | null;
  daysOnStock?: IntegerRange | null;
  car?: FilterCar[];
  fuelType?: string[];
  transmission?: string[];
  bodyType?: string[];
  doorCount?: string[];
  driveType?: string[];
  mileageRange?: IntegerRange | null;
  year?: IntegerRange | null;
  power?: IntegerRange | null;
  cubicCapacity?: IntegerRange | null;
  colors?: FilterColors[];
  featureScore?: FloatRange | null;
  seller?: FilterSeller | null;
};

export type SourcingGetTopSellingApiArg = {
  size?: number;
  body: GetTopSellingRequestBody;
};

export type TopSellingVehicleResponseBody = {
  count: number | null;
  make: TranslationResponseBody | null;
  model: TranslationResponseBody | null;
};

export type SourcingGetTopSellingApiResponse = /** status 200  */ TopSellingVehicleResponseBody[];

export type SourcingGetPriceReportApiResponse = /** status 200  */ PriceReportResponseBody;
export type SourcingGetPriceReportApiArg = {
  externalId?: string;
  country?: string;
};

export type UserListLinksResponseBody = {
  size: number;
  offset: number;
  totalCount?: number | Nullish;
  hasMore?: boolean | Nullish;
};

export type SourcingVehicleListItemResponseBody = {
  favourite: boolean;
  comparison: boolean;
  seen: boolean;
  isNew?: boolean; // Present in list responses, always undefined in detail responses
  inCart: boolean;
  hidden: boolean;
  carvagoBuy: boolean;
  summary: SummaryResponseBody;
  vehicleSummary: VehicleSummaryResponseBody | null;
  sourcingVehicle: SourcingVehicleResponseBody;
  features: {
    [key: string]: FeatureResponseBody;
  };
};

export type FilterBranch = {
  address?: string | null;
  zip?: string | null;
};

export type FilterBuyer = {
  country: string;
  branch?: FilterBranch | null;
};

export type AvailableCarsFilterSeller = {
  country?: string[];
  server?: string[];
  type?: string[];
  ranking?: number | null;
  numberAds?: IntegerRange | null;
  avgNumberAds?: IntegerRange | null;
};

export type AvailableCarsFilterRequestBody = {
  price?: IntegerRange | null;
  daysOnStock?: IntegerRange | null;
  car?: FilterCar[];
  features?: string[];
  fuelType?: string[];
  transmission?: string[];
  bodyType?: string[];
  interiorMaterial?: string[];
  /** Country the vehicle originally comes from (not the seller country). Vehicles without it are excluded. */
  originCountry?: Alpha3[];
  /** Combined with `bodyType` through AND. `null` or omitted = every category. */
  vehicleCategory?: SourcingVehicleCategory | null;
  /** Van body length classes (`LENGTH_L1` … `LENGTH_L4`), OR within, AND with the other filters. */
  vehicleLengthCategory?: string[];
  /** Van body height classes (`HEIGHT_H1` … `HEIGHT_H3`), OR within, AND with the other filters. */
  vehicleHeightCategory?: string[];
  doorCount?: string[];
  driveType?: string[];
  mileageRange?: IntegerRange | null;
  year?: IntegerRange | null;
  power?: IntegerRange | null;
  cubicCapacity?: IntegerRange | null;
  colors?: FilterColors[];
  featureScore?: FloatRange | null;
  trimLevels?: string[];
  seller?: AvailableCarsFilterSeller | null;
  buyer: FilterBuyer;
  margin?: IntegerRange | null;
  marginPercent?: IntegerRange | null;
  pricePercentile?: FloatRange | null;
  position?: FloatRange | null;
  priceGap?: FloatRange | null;
  turnover?: IntegerRange | null;
  numberOfSeats?: IntegerRange | null;
  vatReclaimable?: boolean | null;
  carvagoAvailability?: boolean | null;
  adTitleSearchQuery?: string | null;
  location?: LocationRange | null;
  includeSuspicious?: boolean;
  excludeSuspiciousReasons?: string[];
  includeBlacklist?: boolean;
  onlyHitlist?: boolean;
  filterPresetId?: string | null;
};

export type SourcingVehicleListResponseBody = {
  data: SourcingVehicleListItemResponseBody[];
  links: UserListLinksResponseBody;
  /** Client-only RTK cache field: highest page-aligned offset attempted via infinite scroll. */
  scrollAttemptedOffset?: number;
  /** Client-only RTK cache field: list endpoint returned fewer than ROWS_PER_PAGE (no more server pages). */
  isEndReached?: boolean;
};

export type SourcingVehicleListApiResponse = /** status 200  */ SourcingVehicleListResponseBody;
export type SourcingVehicleListApiArg = {
  page?: number;
  currency?: Alpha3;
  pricingStrategy?: PricingStrategy;
  orderBy?: string | string[] | null;
  body: AvailableCarsFilterRequestBody;
};

export type CarFeaturesResponseBody = {
  featuresScore: number;
  premiumFeatures: string[];
  highlightedFeatures: string[];
};
export type CarFeaturesRequestBody = {
  make: string;
  model: string;
  year: number;
  features: string[];
  compare?: boolean;
  size?: number;
  power?: number;
};
export type GetCarFeaturesApiResponse = /** status 200  */ CarFeaturesResponseBody;
export type GetCarFeaturesApiArg = {
  body: CarFeaturesRequestBody;
};

export type SourcingVehicleCountResponseBody = {
  count: number;
};
export type GetSourcingVehicleCountApiResponse =
  /** status 200  */ SourcingVehicleCountResponseBody;
export type GetSourcingVehicleCountApiArg = {
  currency?: Alpha3;
  pricingStrategy?: PricingStrategy;
  body: AvailableCarsFilterRequestBody;
};

/** Minimal geographic result. The server evaluates the complete filter before applying its limit. */
export type SourcingVehicleLocationsApiResponse = {
  /**
   * Visible-listing total when the map can render coverage. If `limitExceeded` is true,
   * this is Alpha's raw total and is only used to explain why filters need narrowing.
   */
  totalCount: number;
  limit: number;
  limitExceeded: boolean;
  locatedCount: number;
  missingLocationCount: number;
  /** IDs of visible listings without a usable coordinate, included only when map results fit the limit. */
  missingLocationAdIds?: string[];
  /** Effective location filter after resolving saved presets; radius is in Google Maps meters. */
  searchArea: {lat: number; lng: number; radiusMeters: number} | null;
  items: Array<{
    adId: string;
    location: {lat: number; lng: number};
  }>;
};

export type SourcingVehicleLocationsApiArg = {
  currency?: Alpha3;
  pricingStrategy?: PricingStrategy;
  body: AvailableCarsFilterRequestBody;
};

export type SourcingColorResponseBody = {
  color: TranslationResponseBody | null;
  specification: TranslationResponseBody | null;
};

export type ExtraDataResponseBody = {
  marketPosition: number | null;
  marketPositionCount: number | null;
  maximalPossibleMargin: number | null;
  predictedPrice: number | null;
};

export type FeatureResponseBody = {
  key: string;
  isHighlight: boolean;
  translation: string;
};

export type HighlightResponseBody = {
  id: string;
  title: string;
};

export type OccurrenceResponseBody = {
  first: string | null;
  last: string | null;
};

export type SourcingPriceResponseBody = {
  withVat: number | null;
  vatRate: number | null;
  vatReclaimable: boolean | null;
  currency: TranslationResponseBody | null;
  originalCurrency: TranslationResponseBody | null;
  originalWithVat: number | null;
};

export type RatingResponseBody = {
  average: number | null;
  count: number | null;
};

export type SellerResponseBody = {
  name: string | null;
  type: TranslationResponseBody | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  email: string | null;
  phone: string | null;
  rating: RatingResponseBody | null;
  country: TranslationResponseBody | null;
};

export type ShortcomingResponseBody = {
  id: string;
  title: string;
};

export type SimilarityResponseBody = {
  level: number | null;
  score: number | null;
};

export type UrlResponseBody = {
  full: string | null;
};

/**
 * Codes the data pipeline can put on a sourcing vehicle. The data team maintains the list in
 * the shared codelist and extends it over time; every code has a label under
 * `entity.sourcing.suspiciousReason.*`.
 */
export type SourcingSuspiciousReason =
  | 'suspicious_price_low'
  | 'suspicious_price_high'
  | 'suspicious_mileage_low'
  | 'suspicious_mileage_high'
  | 'suspicious_dod'
  | 'manufactured_in_future'
  | 'leasing'
  | 'quality_flag_missing_model_family'
  | 'quality_flag_missing_car_style'
  | 'quality_flag_car_style_other'
  | 'quality_flag_missing_drive'
  | 'quality_flag_missing_power'
  | 'quality_flag_power_out_of_range'
  | 'quality_flag_missing_mileage'
  | 'quality_flag_mileage_out_of_range'
  | 'quality_flag_missing_price_with_vat'
  | 'quality_flag_missing_transmission'
  | 'quality_flag_missing_fuel_type'
  | 'quality_flag_not_in_selected_fuel_types'
  | 'quality_flag_missing_year'
  | 'quality_flag_year_out_of_range'
  | 'quality_flag_missing_seller_country'
  | 'quality_flag_not_road_worthy_or_damaged'
  | 'quality_flag_abnormal_ad_duration'
  | 'quality_flag_future_car'
  | 'quality_flag_old_but_not_sold'
  | 'quality_flag_banished_seller'
  | 'quality_flag_detected_problems_in_description'
  | 'quality_flag_same_id_different_car'
  | 'mileage_outlier_action_remove_immediately'
  | 'mileage_outlier_action_remove_high_confidence'
  | 'mileage_outlier_action_remove_medium_confidence'
  | 'mileage_outlier_action_remove_logz_decides'
  | 'mileage_outlier_action_keep_review'
  | 'price_business_rule_invalid_non_positive_price'
  | 'price_business_rule_invalid_missing_mileage'
  | 'price_business_rule_invalid_negative_mileage'
  | 'price_business_rule_invalid_year_out_of_range'
  | 'price_business_rule_invalid_power_out_of_range'
  | 'price_business_rule_suspicious_very_low_price'
  | 'price_business_rule_suspicious_low_price_new_car'
  | 'price_business_rule_suspicious_high_price_non_luxury'
  | 'price_business_rule_suspicious_old_car_high_price'
  | 'data_quality_flag_actual_price_too_low'
  | 'data_quality_flag_actual_price_too_high'
  | 'data_quality_flag_actual_to_prediction_extremely_high'
  | 'data_quality_flag_actual_to_prediction_extremely_low'
  | 'data_quality_flag_actual_to_cb_prediction_extremely_high'
  | 'data_quality_flag_actual_to_cb_prediction_extremely_low'
  | 'prediction_outlier_type_overpriced_outlier'
  | 'prediction_outlier_type_underpriced_outlier'
  | 'prediction_outlier_type_overpriced_outlier_cb_only'
  | 'prediction_outlier_type_underpriced_outlier_cb_only'
  | 'technical_part_replacement_needed'
  | 'technical_unspecified_noise'
  | 'technical_repaired_accident_damage'
  | 'technical_collision_damage'
  | 'technical_non_functional'
  | 'technical_minor_problems'
  | 'price_conditional_on_financing'
  | 'price_conditional_on_leasing'
  | 'price_conditional_on_trade_in_or_scrappage'
  | 'price_conditional_on_domestic_sale'
  | 'price_excluding_vat'
  | 'price_is_for_export_only'
  | 'price_leasing'
  | 'price_financing_offer'
  | 'other_car_is_not_available_now'
  | 'other_car_is_reserved'
  | 'other_tuning'
  | 'other_taxi_driving_school_military_use'
  | 'other_battery_rental'
  | 'other_auction'
  | 'other_car_without_registration'
  | 'other_origin_outside_europe';

export type SourcingVehicleResponseBody = {
  id: string;
  adId: string;
  isAvailable: boolean;
  isSold: boolean | null;
  /**
   * Live check-car / availability-check result. Independent of occurrence-based
   * `isAvailable` / `isSold`: `true` = confirmed gone, `false` = confirmed active,
   * `null` = never checked.
   */
  justSold: boolean | null;
  carStyle: TranslationResponseBody | null;
  /** CO₂ in g/km. Does not say whether it follows the WLTP or the older NEDC cycle. */
  carbonDioxideEmission: number | null;
  /** Where the emission value came from: the ad itself, or the ADAC catalogue. */
  carbonDioxideEmissionOrigin: 'ad' | 'adac_emissions' | null;
  color: SourcingColorResponseBody | null;
  cubicCapacity: number | null;
  daysOnStock: number | null;
  doorCount: TranslationResponseBody | null;
  driveType: TranslationResponseBody | null;
  featureScore: number | null;
  features: TranslationResponseBody[];
  trimLevel: string | null;
  fuelType: TranslationResponseBody | null;
  highlightFeatures: TranslationResponseBody[];
  interiorMaterial: TranslationResponseBody | null;
  make: TranslationResponseBody | null;
  mileage: number | null;
  model: TranslationResponseBody | null;
  modelFamilyGroup: TranslationResponseBody | null;
  numberOfSeats: number | null;
  occurrence: OccurrenceResponseBody | null;
  originCountry: TranslationResponseBody | null;
  power: number | null;
  price: SourcingPriceResponseBody | null;
  seller: SellerResponseBody | null;
  server: string | null;
  similarity: SimilarityResponseBody | null;
  /** Date of first registration. Sources often publish only the month and year. */
  registrationDate: string | null;
  manufactureDate: string | null;
  technicalInspectionValidTo: string | null;
  thumbnailUrl: UrlResponseBody | null;
  transmission: TranslationResponseBody | null;
  turnover: number | null;
  url: UrlResponseBody | null;
  proxyUrl: UrlResponseBody | null;
  vin: string | null;
  year: number | null;
  maximalPossibleMargin: number | null;
  extraData: ExtraDataResponseBody | null;
  adTitle: string | null;
  description: string | null;
  pictureUrls: string[];
  suspicious: boolean;
  suspiciousReason: SourcingSuspiciousReason[];
  attractivenessScore: number | null;
  marketSituationCategory:
    | (
        | 'MARKET_SITUATION_MAJOR_SHORTAGE'
        | 'MARKET_SITUATION_SURPLUS'
        | 'MARKET_SITUATION_STABLE'
        | 'MARKET_SITUATION_SHORTAGE'
      )
    | null;
  priceDynamicsCategory:
    ('PRICE_DYNAMICS_FALLING' | 'PRICE_DYNAMICS_RISING' | 'PRICE_DYNAMICS_STABLE') | null;
  isBlacklist: boolean;
  isHitlist: boolean;
  priceChanges: number[];
  priceChangesDates: string[];
  pricePercentile: number | null;
};

export type SummaryResponseBody = {
  position: number | null;
  positionFrom: number | null;
  margin: number | null;
  original: number | null;
  purchase: number | null;
  retail: number | null;
  lid: number | null;
  dod: number | null;
  rank: number | null;
  vehicleCosts: number | null;
  adPrice: number | null;
};
export type VehicleSummaryResponseBody = {
  id?: string | null;
  purchasePrice: number | null;
  sellingPrice: number | null;
  comment: string | null;
};
export type SourcingVehicleDetailResponseBody = {
  favourite: boolean;
  comparison: boolean;
  seen: boolean;
  isNew?: boolean; // Always undefined in detail responses (only present in list responses)
  inCart: boolean;
  hidden: boolean;
  carvagoBuy: boolean;
  summary: SummaryResponseBody;
  vehicleSummary: VehicleSummaryResponseBody | null;
  sourcingVehicle: SourcingVehicleResponseBody;
  features: {
    [key: string]: FeatureResponseBody;
  };
  highlights: HighlightResponseBody[];
  shortcomings: ShortcomingResponseBody[];
};

export type SourcingVehicleDetailApiResponse = /** status 200  */ SourcingVehicleDetailResponseBody;
export type SourcingVehicleDetailApiArg = {
  externalId: string;
  marginLte?: number | null;
  marginGte?: number | null;
  positionGte?: number | null;
  positionLte?: number | null;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  pricingStrategy?: PricingStrategy;
};

export type TopBarResponseBody = {
  countries: Alpha3[];
  currencies: Alpha3[];
};
export type AllowedCountryAndCurrencyApiResponse = /** status 200  */ TopBarResponseBody;
export type AllowedCountryAndCurrencyApiArg = void;

export type ListUserResponseBody = {
  data: SourcingVehicleDetailResponseBody[];
  links: UserListLinksResponseBody;
};
export type SourcingUserList =
  'user-comparison' | 'user-favourite' | 'user-hidden' | 'user-to-buy' | 'user-carvago-buy';
export type SourcingUserListQuery = Omit<SourcingUserListApiArg, 'size' | 'offset'> & {
  list: SourcingUserList;
};
export type SourcingUserListApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  pricingStrategy?: PricingStrategy;
  margin?: number;
  orderBy?: string;
};

export type BulkDeleteUserFavouriteRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserFavouriteRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserFavouriteApiResponse = unknown;
export type BulkDeleteUserFavouriteApiArg = {
  body: BulkDeleteUserFavouriteRequestBody;
};
export type BulkCreateUserFavouriteApiResponse = unknown;
export type BulkCreateUserFavouriteApiArg = {
  body: BulkCreateUserFavouriteRequestBody;
};
export type BulkDeleteUserComparisonRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserComparisonRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserComparisonApiResponse = unknown;
export type BulkDeleteUserComparisonApiArg = {
  body: BulkDeleteUserComparisonRequestBody;
};
export type BulkCreateUserComparisonApiResponse = unknown;
export type BulkCreateUserComparisonApiArg = {
  body: BulkCreateUserComparisonRequestBody;
};
export type BulkDeleteUserHiddenRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserHiddenRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserHiddenApiResponse = unknown;
export type BulkDeleteUserHiddenApiArg = {
  body: BulkDeleteUserHiddenRequestBody;
};
export type BulkCreateUserHiddenApiResponse = unknown;
export type BulkCreateUserHiddenApiArg = {
  body: BulkCreateUserHiddenRequestBody;
};
export type BulkDeleteUserToBuyRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserToBuyRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserToBuyApiResponse = unknown;
export type BulkDeleteUserToBuyApiArg = {
  body: BulkDeleteUserToBuyRequestBody;
};
export type BulkCreateUserToBuyApiResponse = unknown;
export type BulkCreateUserToBuyApiArg = {
  body: BulkCreateUserToBuyRequestBody;
};

export type DrillDownLevelName =
  | 'make'
  | 'model_family_group'
  | 'model'
  | 'year'
  | 'power'
  | 'fuel_type'
  | 'transmission'
  | 'drive'
  | 'mileage_range'
  | 'car_style'
  | 'vat_reclaimable'
  | 'number_of_seats'
  | 'trim_level';

export type DrillDownLevel = {
  level: DrillDownLevelName;
  values: string[];
  priority: number;
};

export type DrillDownStatisticsLevel = {
  name: DrillDownLevelName;
  value: string;
};

export type DrillDownStatisticsItem = {
  type: 'sold' | 'stock';
  amount: number;
  mileage: number;
  price: number;
};

export type DrillDownApiArg = {
  levels: DrillDownLevel[];
  currency: string;
  regions: string[];
};
export type DrillDownStatistics = {
  levels: DrillDownStatisticsLevel[];
  statistics: DrillDownStatisticsItem[];
};
export type DrillDownResponse = {
  translations: Record<string, string>;
  statisticsAggregated: DrillDownStatistics[];
};
/** Alfa may return null for a requested level before the response workaround normalizes it. */
export type RawDrillDownResponse = Omit<DrillDownResponse, 'statisticsAggregated'> & {
  statisticsAggregated: (Omit<DrillDownStatistics, 'levels'> & {
    levels: (Omit<DrillDownStatisticsLevel, 'value'> & {value: string | null})[];
  })[];
};

export type CreateUserVehicleSummaryRequestBody = {
  comment?: string | null;
  purchasePrice?: number | null;
  sellingPrice?: number | null;
};
export type CreateUserVehicleSummaryApiArg = {
  sourcingVehicleId: string;
  body: CreateUserVehicleSummaryRequestBody;
};
export type SourcingUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};
export type SalesClaim = {
  id: string;
  note: string;
  author: SourcingUser;
  createdAt: string;
};
export type SourcingServer = {
  code: string;
  name: string;
  preferred: boolean;
};
export type SortUserComparisonRequestApiArg = SourcingVehicleDetailResponseBody[];
type ExternalIdWithPositionRequestBody = {
  externalId: string;
  position: number;
};
export type SortUserComparisonRequestBody = ExternalIdWithPositionRequestBody[];
export type PushVehiclesToSaleApiArg = {
  externalIds: string[];
};
/** The selection export takes the whole-list export's parameters plus the selected vehicles. */
export type ExportVehiclesToCsvApiArg = ExportAllUserToBuyToCsvApiArg & {
  externalIds: string[];
};
export type SourcingCommentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  external: boolean;
};
export type SourcingComment = {
  uuid: string;
  message: string;
  parent: {
    uuid: string;
  } | null;
  author: SourcingCommentUser;
  createdAt: string;
};
export type SourcingGetCommentsApiResponse = SourcingComment[];
export type SourcingGetCommentsApiArg = {
  externalId: string;
};
export type SourcingCreateCommentApiResponse = SourcingGetCommentsApiResponse;
export type SourcingCreateCommentRequestBody = {
  message: string;
  parentCommentUuid?: string | null;
};
export type SourcingCreateCommentApiArg = {
  externalId: string;
  body: SourcingCreateCommentRequestBody;
};
export type SourcingDeleteCommentApiArg = {
  externalId: string;
  uuid: string;
};
export type SourcingUpdateCommentApiResponse = SourcingGetCommentsApiResponse;
export type SourcingUpdateCommentRequestBody = SourcingCreateCommentRequestBody;
export type SourcingUpdateCommentApiArg = {
  externalId: string;
  uuid: string;
  body: SourcingUpdateCommentRequestBody;
};
export type DetailUserCostVehicleNoteApiResponse = /** status 200  */ CostVehicleNoteResponseBody;
export type DetailUserCostVehicleNoteApiArg = {
  sourcingVehicleId: string;
};
export type SetUserVehicleCostNoteApiArg = {
  sourcingVehicleId: string;
  updateCostVehicleNoteRequestBody: UpdateCostVehicleNoteRequestBody;
};
export type CostVehicleNoteResponseBody = {
  uuid: string;
  note: string;
};
export type UpdateCostVehicleNoteRequestBody = {
  note: string;
};
export type DetailDefaultUserPresetCostApiResponse =
  /** status 200  */ CostPresetDetailResponseBody;
export type CreateUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  userVehicleCostRequestBody: UserVehicleCostRequestBody;
};
export type CreateUserVehicleCostsApiArg = {
  sourcingVehicleId: string;
  userVehicleCostsRequestBody: UserVehicleCostsRequestBody;
};
export type UserCostVehicleListApiResponse = /** status 200  */ CostDetailItemResponseBody[];
export type UserCostVehicleListApiArg = {
  sourcingVehicleId: string;
};
export type DeleteUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  itemUuid: string;
};
export type UpdateUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  itemUuid: string;
  userVehicleCostRequestBody: UserVehicleCostRequestBody;
};
export type CostDetailItemResponseBody = {
  uuid: string;
  title: string;
  type: 'fixed' | 'other' | 'important';
  cost: number | null;
  currency: string | null;
  note: string | null;
  enabled: boolean;
};
export type CostPresetDetailResponseBody = {
  uuid: string;
  title: string;
  note: string | null;
  default: boolean;
  presetItems: CostDetailItemResponseBody[];
};
export type CostItem = {
  title: string;
  type: 'fixed' | 'other' | 'important';
  cost: string;
  currency: string;
  note: string;
  enabled: boolean;
};
export type UserVehicleCostRequestBody = {
  costItem: CostItem;
};
export type UserVehicleCostsRequestBody = {
  costItems: CostItem[];
};
export type RemoveHighlightBulkApiResponse = /** status 204  */ void;
export type RemoveHighlightBulkApiArg = {
  externalId: string;
  removeHighlightBulkRequestBody: RemoveHighlightBulkRequestBody;
};
export type RemoveShortcomingBulkApiResponse = /** status 204  */ void;
export type RemoveShortcomingBulkApiArg = {
  externalId: string;
  removeShortcomingBulkRequestBody: RemoveShortcomingBulkRequestBody;
};
export type RemoveShortcomingBulkRequestBody = {
  shortcomingIds: string[];
};
export type RemoveHighlightBulkRequestBody = {
  highlightIds: string[];
};

export type AdServerRedirectApiArg = {
  externalId?: string;
  serverName?: string;
  buyerCountry?: Alpha3;
  sellerCountry?: Alpha3;
  currency?: Currency;
  relativeFilterId?: string | null;
};
export type AdServerRedirectResponse = {
  url: string;
};
/** Ad server search for one drill down row, built from the levels of its path. */
export type DrillDownAdServerRedirectRequestBody = {
  serverName: string;
  sellerCountry?: Alpha3;
  currency?: Currency | null;
  /** Catalogue key, e.g. `MAKE_SKODA`. */
  make: string;
  /**
   * Catalogue key of the model family (`MAKE_*-MODELFAMILY_*`), i.e. the drill down `model` level
   * value, or a `model_family_group` value that is itself a model family.
   */
  modelFamily: string;
  year?: IntegerRange | null;
  /** In kW. */
  power?: IntegerRange | null;
  /** In km. */
  mileage?: IntegerRange | null;
  fuelType?: string[] | null;
  transmission?: string[] | null;
  drive?: string[] | null;
  carStyle?: string[] | null;
  trimLevel?: string[] | null;
  /** `true` narrows to VAT deductible ads; omitted applies no VAT filter. */
  vatReclaimable?: boolean;
};
export type DrillDownAdServerRedirectApiResponse = /** status 200  */ AdServerRedirectResponse;
export type DrillDownAdServerRedirectApiArg = {
  drillDownAdServerRedirectRequestBody: DrillDownAdServerRedirectRequestBody;
};
export type CreateRelativeFilterApiResponse = /** status 201  */ UuidResponseBody;
export type CreateRelativeFilterApiArg = {
  createRelativeFilterRequestBody: CreateRelativeFilterRequestBody;
};
export type GetRelativeFiltersApiResponse = /** status 200  */ RelativeFilterResponseBody[];
export type GetRelativeFiltersApiArg = void;
export type DeleteRelativeFilterApiResponse = /** status 204  */ void;
export type DeleteRelativeFilterApiArg = {
  id: string;
};
export type UpdateRelativeFilterApiResponse = /** status 204  */ void;
export type UpdateRelativeFilterApiArg = {
  id: string;
  updateRelativeFilterRequestBody: UpdateRelativeFilterRequestBody;
};
export type ReorderRelativeFiltersApiResponse = /** status 204  */ void;
export type ReorderRelativeFiltersApiArg = {
  reorderRelativeFiltersRequestBody: ReorderRelativeFiltersRequestBody;
};
export type NumericBoundBody = {
  from: number | null;
  to: number | null;
};
export type RelativeFilterChoice = 'same' | 'same_better';
export type RelativeFilterConfigBody = {
  year: NumericBoundBody | null;
  mileage: NumericBoundBody | null;
  mileagePct: NumericBoundBody | null;
  power: NumericBoundBody | null;
  powerPct: NumericBoundBody | null;
  price: NumericBoundBody | null;
  pricePct: NumericBoundBody | null;
  featureScore: NumericBoundBody | null;
  trimLevel: RelativeFilterChoice | null;
  transmission: RelativeFilterChoice | null;
  fuel: RelativeFilterChoice | null;
  body: RelativeFilterChoice | null;
  drive: RelativeFilterChoice | null;
  vatDeductible: RelativeFilterChoice | null;
  sellerType: string[] | null;
  seats: RelativeFilterChoice | null;
  radius: number | null;
  features: string[] | null;
  /** Snap resolved mileage bounds to the ad server's filter steps (price-report resolves on FE, sourcing on BE). */
  mileageSnapToServerSteps?: boolean | null;
  /** Absolute lower mileage limit in km; never applied above the vehicle's own mileage. */
  mileageMin?: number | null;
  /**
   * Ad-server codes the compared ads are restricted to. Resolved on the FE by price-report
   * only; sourcing stores and returns the value untouched (T20-138339).
   */
  server?: string[] | null;
};
/** Which datasets the report shows; a sibling of `config`, not part of it. */
export type RelativeFilterDatasetsBody = {
  showStockCars: boolean;
  showSoldIn30: boolean;
  showSoldIn90: boolean;
};
export type CreateRelativeFilterRequestBody = {
  name: string;
  config: RelativeFilterConfigBody;
  /** Omitted or null stores all three datasets on. */
  datasets?: RelativeFilterDatasetsBody | null;
  default?: boolean | null;
};
export type RelativeFilterResponseBody = {
  id: string;
  name: string;
  config: RelativeFilterConfigBody;
  /** Always present; presets saved before datasets existed return all three on. */
  datasets: RelativeFilterDatasetsBody;
  position: number;
  system: boolean;
  default: boolean;
  createdAt: string;
  updatedAt: string;
};
export type UpdateRelativeFilterRequestBody = {
  name?: string;
  config?: RelativeFilterConfigBody;
  /** Omitted keeps the stored datasets; when sent, all three flags are required. */
  datasets?: RelativeFilterDatasetsBody;
  default?: boolean;
};
export type OrderedRelativeFilterItem = {
  id: string;
  position: number;
};
export type ReorderRelativeFiltersRequestBody = {
  items: OrderedRelativeFilterItem[];
};
export type LocationRange = {
  lat: number;
  lon: number;
  distance: number;
};
export type CreateUserSeenApiResponse = unknown;
export type CreateUserSeenApiArg = {
  body: CreateUserSeenRequestBody;
};
export type CreateUserSeenRequestBody = {
  externalId: string;
};
export type GetPriceMapApiResponse = /** status 200  */ PriceMapResponseBody;
export type GetPriceMapApiArg = {
  externalId: string;
  priceMapRequestBody: PriceMapRequestBody;
};
export type LocationResponseBody = {
  lat?: number | null;
  lon?: number | null;
};
export type PriceMapCarResponseBody = {
  ad_id: string | null;
  days_on_stock: number | null;
  feature_score: number | null;
  first_scraped_price_currency: string | null;
  first_scraped_price_with_vat: number | null;
  is_available: boolean | null;
  is_sold: boolean | null;
  just_sold?: boolean | null;
  first_occurrence: string | null;
  last_occurrence: string | null;
  location: LocationResponseBody | null;
  mileage: number | null;
  original_currency: string | null;
  original_price_with_vat: number | null;
  price_with_vat: number | null;
  suspicious: boolean | null;
};
export type PriceMapResponseBody = {
  total_hits: number | null;
  total_pages: number | null;
  current_page: number | null;
  query_price_position: number | null;
  query_price_index: number | null;
  query_original_price_index: number | null;
  cars: PriceMapCarResponseBody[];
};
export type IntegerRangeFilter = {
  gte?: number | null;
  lte?: number | null;
};
export type FloatRangeFilter = {
  gte?: number | null;
  lte?: number | null;
};
export type LocationFilter = {
  lat?: number | null;
  lon?: number | null;
  distance?: number | null;
};
export type PriceMapRequestBody = {
  size: number;
  query_price: number;
  offset?: number | null;
  fuelType?: string[] | null;
  transmission?: string[] | null;
  bodyStyle?: string[] | null;
  drive?: string[] | null;
  mileage?: IntegerRangeFilter | null;
  firstRegistrationOnYear?: IntegerRangeFilter | null;
  power?: IntegerRangeFilter | null;
  price?: IntegerRangeFilter | null;
  daysOnStock?: IntegerRangeFilter | null;
  featuresLevel?: FloatRangeFilter | null;
  trimLevel?: string[] | null;
  isVatDeductible?: boolean | null;
  hideSuspiciousCars?: boolean | null;
  hideSoldCars?: boolean | null;
  hideStockCars?: boolean | null;
  hideSoldIn30?: boolean | null;
  hideSoldIn90?: boolean | null;
  location?: LocationFilter | null;
  title?: string | null;
};
export type SourcingFilterpresetsPresetNotificationsDisableApiResponse = unknown;
export type SourcingFilterpresetsPresetNotificationsDisableApiArg = {
  id: string;
};
export type SourcingFilterpresetsPresetNotificationsEnableApiResponse = unknown;
export type SourcingFilterpresetsPresetNotificationsEnableApiArg = {
  id: string;
};
export type CreateSourcingVehicleOfferApiResponse =
  /** status 200  */ SourcingVehicleOfferResponseBody;
export type CreateSourcingVehicleOfferApiArg = {
  createSourcingVehicleOfferRequestBody: CreateSourcingVehicleOfferRequestBody;
};
export type GetSourcingVehicleOfferApiResponse =
  /** status 200  */ SourcingVehicleOfferResponseBody;
export type GetSourcingVehicleOfferApiArg = {
  offerId: string;
};
export type SourcingVehicleOfferResponseBody = {
  id: string;
  externalId: string;
};
export type CreateSourcingVehicleOfferRequestBody = {
  externalId: string;
};

export type BulkDeleteUserCarvagoBuyApiResponse = unknown;
export type BulkDeleteUserCarvagoBuyApiArg = {
  bulkDeleteUserCarvagoBuyRequestBody: BulkDeleteUserCarvagoBuyRequestBody;
};
export type BulkCreateUserCarvagoBuyApiResponse = /** status 204  */ void;
export type BulkCreateUserCarvagoBuyApiArg = {
  bulkCreateUserCarvagoBuyRequestBody: BulkCreateUserCarvagoBuyRequestBody;
};
export type DeleteUserCarvagoBuyApiResponse = unknown;
export type DeleteUserCarvagoBuyApiArg = {
  externalId: string;
};
export type CreateUserCarvagoBuyApiResponse = /** status 204  */ void;
export type CreateUserCarvagoBuyApiArg = {
  createUserCarvagoBuyRequestBody: CreateUserCarvagoBuyRequestBody;
};
export type BulkDeleteUserCarvagoBuyRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserCarvagoBuyRequestBody = {
  externalIds: string[];
};
export type CreateUserCarvagoBuyRequestBody = {
  externalId: string;
};

export type AvailabilityCheckStatus = 'active' | 'just_sold' | 'unknown' | 'unsupported_platform';

export type AvailabilityCheckResultItem = {
  adId: string;
  status: AvailabilityCheckStatus;
};

export type AvailabilityCheckRequestBody = {
  adIds: string[];
};

export type AvailabilityCheckResponseBody = {
  results: AvailabilityCheckResultItem[];
};

export type SourcingAvailabilityCheckApiArg = AvailabilityCheckRequestBody;
export type SourcingAvailabilityCheckApiResponse = AvailabilityCheckResponseBody;

export type SourcingAvailabilityByAdIdApiArg = {
  adId: string;
};
export type SourcingAvailabilityByAdIdApiResponse = AvailabilityCheckResultItem;
